import React from "react";
import { Avatar, Box, Group, Stack, Text, UnstyledButton } from "@mantine/core";
import { formatCurrency } from "../../../utils/cashDrawerUtils";
import { PaymentMethodIcon, methodLabel } from "./paymentMethodIcon";
import styles from "./sales.module.css";

/** Uppercase section label, optionally with a `· n` count. */
export function SectionLabel({
  children,
  count,
}: {
  children: React.ReactNode;
  count?: number;
}) {
  return (
    <Text className={styles.sectionLabel}>
      {children}
      {count != null ? ` · ${count}` : ""}
    </Text>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts[0][0] + (parts[1]?.[0] ?? "")).toUpperCase();
}

interface ClientBlockProps {
  name: string;
  phone?: string;
  email?: string;
  /** Purchases screen: a "Transaction #…" link under the contact lines. */
  txnRef?: string;
  onTxnClick?: () => void;
}

/** Client identity block, identical on both screens. */
export function ClientBlock({
  name,
  phone,
  email,
  txnRef,
  onTxnClick,
}: ClientBlockProps) {
  return (
    <Group gap={12} align="flex-start" wrap="nowrap">
      <Avatar
        radius="xl"
        size={44}
        styles={{
          placeholder: {
            background: "#e3f2f6",
            color: "#1a7285",
            fontSize: 14,
            fontWeight: 600,
          },
        }}
      >
        {initials(name)}
      </Avatar>
      <Box style={{ minWidth: 0 }}>
        <Text className={styles.clientName}>{name}</Text>
        {phone && <Text className={styles.clientMeta}>{phone}</Text>}
        {email && (
          <Text
            className={styles.clientMeta}
            style={{ wordBreak: "break-all" }}
          >
            {email}
          </Text>
        )}
        {txnRef &&
          (onTxnClick ? (
            <UnstyledButton className={styles.txnLink} onClick={onTxnClick}>
              Transaction #{txnRef}
            </UnstyledButton>
          ) : (
            <Text className={styles.txnLink}>Transaction #{txnRef}</Text>
          ))}
      </Box>
    </Group>
  );
}

interface LineItemRowProps {
  name: string;
  staff?: string;
  amount: number;
  first?: boolean;
  /** Product quantity (>1) — shows "{qty} × {unit}" under the name. */
  qty?: number;
  unitPrice?: number;
}

/**
 * Read-only line item (name + `with {staff}`, amount). Refunds are
 * payment-level in this build (not per line), so there is deliberately no
 * per-line refund action or refunded strike-through state here.
 */
export function LineItemRow({
  name,
  staff,
  amount,
  first,
  qty,
  unitPrice,
}: LineItemRowProps) {
  const sub = staff
    ? `with ${staff}`
    : qty
      ? `${qty} × ${formatCurrency(unitPrice ?? amount / qty)}`
      : undefined;
  return (
    <Box className={`${styles.lineRow} ${first ? styles.lineRowFirst : ""}`}>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Text className={styles.lineName}>{name}</Text>
        {sub && <Text className={styles.lineSub}>{sub}</Text>}
      </Box>
      <Text className={styles.lineAmount}>{formatCurrency(amount)}</Text>
    </Box>
  );
}

interface PaymentCardProps {
  method: string;
  /** Meta line, e.g. "Deposit · 1:33 PM" or "Balance · Captured". */
  meta: string;
  amount: number;
  /**
   * Raw method string driving the tender icon (e.g. "Stripe_Card", "Cash").
   * Falls back to `method` when omitted.
   */
  iconMethod?: string;
  /**
   * Card brand for card tenders (e.g. "Visa"). When present with `last4`, it
   * replaces `method` as the label (e.g. "Visa ••1234"). Default off.
   */
  brand?: string;
  /**
   * Last 4 digits of the card. When present, appended to the method line as
   * `••1234`. Default off = current method-only behavior.
   */
  last4?: string;
  /** Renders the returned-arrow icon in the danger color for refund rows. */
  isRefund?: boolean;
  /** Amount already refunded against this payment; shown as a red sub-line. */
  refundedAmount?: number;
  /** Per-payment refund action (override #1: refunds are payment-level). */
  onRefund?: () => void;
  refundDisabled?: boolean;
  refundLabel?: string;
}

/** Bordered card, one per payment. */
export function PaymentCard({
  method,
  meta,
  amount,
  iconMethod,
  brand,
  last4,
  isRefund = false,
  refundedAmount,
  onRefund,
  refundDisabled,
  refundLabel = "Refund",
}: PaymentCardProps) {
  // Shared card label so the drawer matches the Payments list exactly
  // (capitalized brand + spaced `•• 1234`); non-card tenders keep `method`.
  const label = methodLabel(iconMethod ?? method, method, brand, last4);
  return (
    <Box className={styles.paymentCard}>
      <PaymentMethodIcon
        method={iconMethod ?? method}
        isRefund={isRefund}
        size={17}
        color={isRefund ? "#d92d20" : "#555"}
      />
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Text className={styles.paymentMethod}>{label}</Text>
        <Text className={styles.paymentMeta}>{meta}</Text>
        {refundedAmount != null && refundedAmount > 0 && (
          <Text className={styles.paymentRefunded}>
            Refunded −{formatCurrency(refundedAmount)}
          </Text>
        )}
      </Box>
      <Stack gap={4} align="flex-end">
        <Text
          className={`${styles.paymentAmount} ${
            amount < 0 ? styles.paymentAmountNeg : ""
          }`}
        >
          {formatCurrency(amount)}
        </Text>
        {onRefund && !refundDisabled && (
          <UnstyledButton
            onClick={onRefund}
            style={{
              fontSize: 12.5,
              fontWeight: 500,
              color: "#1a7285",
            }}
          >
            {refundLabel}
          </UnstyledButton>
        )}
      </Stack>
    </Box>
  );
}

/** Label/value row for the Purchases "TRANSACTION" totals section. */
export function KVRow({ label, value }: { label: string; value: string }) {
  return (
    <Box className={styles.kvRow}>
      <Text component="span" className={styles.kvLabel}>
        {label}
      </Text>
      <Text component="span" className={styles.kvValue}>
        {value}
      </Text>
    </Box>
  );
}
